import React, { useEffect } from 'react'
import { ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Text } from '../../components/ui'
import type {
  MarketingClipping,
  MarketingClippingOrigin,
  MarketingNewsAlertSentiment,
} from '../../types/marketing'

interface ClippingDetalheModalProps {
  clipping: MarketingClipping
  searchConfigName?: string | null
  onClose: () => void
}

const SENTIMENT_LABELS: Record<MarketingNewsAlertSentiment, string> = {
  positivo: 'Positivo',
  neutro: 'Neutro',
  negativo: 'Negativo',
}

function formatDateBr(value: string | null | undefined): string {
  if (!value) return '—'
  const [y, m, d] = value.slice(0, 10).split('-')
  if (!y || !m || !d) return value
  return `${d}/${m}/${y}`
}

function formatDateTimeBr(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value.slice(0, 10)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function displayValue(value: string | number | null | undefined): string {
  if (value == null) return '—'
  const text = String(value).trim()
  return text.length > 0 ? text : '—'
}

function originLabel(origin: MarketingClippingOrigin): string {
  return origin === 'ia' ? 'IA' : 'Manual'
}

const DetailField: React.FC<{ label: string; value: React.ReactNode; className?: string }> = ({
  label,
  value,
  className,
}) => (
  <div className={className}>
    <Text size="xs" color="muted" className="uppercase tracking-wider">
      {label}
    </Text>
    <div className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
      {value ?? '—'}
    </div>
  </div>
)

const ClippingDetalheModal: React.FC<ClippingDetalheModalProps> = ({
  clipping,
  searchConfigName,
  onClose,
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const items = clipping.items || []

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="clipping-detalhe-titulo"
      >
        <div className="flex items-start justify-between gap-4 p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <Heading
              as="h2"
              size="2xl"
              id="clipping-detalhe-titulo"
              className="text-gray-900 dark:text-white"
            >
              Detalhes do clipping
            </Heading>
            <Text size="sm" color="muted" className="mt-1 line-clamp-2">
              {clipping.title}
            </Text>
          </div>
          <Button
            onClick={onClose}
            variant="outline"
            size="sm"
            className="flex items-center gap-1 shrink-0"
          >
            <XMarkIcon className="h-4 w-4" />
            Fechar
          </Button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailField label="Título" value={displayValue(clipping.title)} className="md:col-span-2" />
            <DetailField label="Data" value={formatDateBr(clipping.clipping_date)} />
            <DetailField label="Categoria" value={displayValue(clipping.category?.name)} />
            <DetailField
              label="Origem"
              value={
                <span
                  className={
                    clipping.origin === 'ia'
                      ? 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
                      : 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }
                >
                  {originLabel(clipping.origin)}
                </span>
              }
            />
            <DetailField
              label="Configuração de busca"
              value={displayValue(searchConfigName || clipping.search_config?.name)}
            />
            <DetailField
              label="Origem de alerta"
              value={clipping.source_alert_id ? 'Sim' : 'Não'}
            />
            <DetailField label="Criado em" value={formatDateTimeBr(clipping.created_at)} />
            <DetailField label="Atualizado em" value={formatDateTimeBr(clipping.updated_at)} />

            <DetailField
              label="Observações"
              value={displayValue(clipping.notes)}
              className="md:col-span-2"
            />

            <div className="md:col-span-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                Análise de IA
              </Text>
              {!clipping.analysis && (
                <Text size="sm" color="muted" className="mt-1">
                  Nenhuma análise registrada para este clipping.
                </Text>
              )}
            </div>

            {clipping.analysis && (
              <>
                <DetailField
                  label="Sugestão IA"
                  value={
                    clipping.analysis.is_clipping == null
                      ? '—'
                      : clipping.analysis.is_clipping
                        ? 'É clipping'
                        : 'Não é clipping'
                  }
                />
                <DetailField
                  label="Score"
                  value={
                    clipping.analysis.relevance_score != null
                      ? String(clipping.analysis.relevance_score)
                      : '—'
                  }
                />
                <DetailField
                  label="Sentimento"
                  value={
                    clipping.analysis.sentiment
                      ? SENTIMENT_LABELS[clipping.analysis.sentiment]
                      : '—'
                  }
                />
                <DetailField
                  label="Categoria sugerida"
                  value={displayValue(clipping.analysis.suggested_category?.name)}
                />
                <DetailField
                  label="Analisado em"
                  value={formatDateTimeBr(clipping.analysis.analyzed_at)}
                />
                <DetailField
                  label="Resumo da IA"
                  value={displayValue(clipping.analysis.summary)}
                  className="md:col-span-2"
                />
                <DetailField
                  label="Justificativa da IA"
                  value={displayValue(clipping.analysis.reason)}
                  className="md:col-span-2"
                />
              </>
            )}

            <div className="md:col-span-2">
              <Text size="xs" color="muted" className="uppercase tracking-wider">
                Veículos / Links
              </Text>
              {items.length === 0 ? (
                <div className="mt-1 text-sm text-gray-900 dark:text-white">—</div>
              ) : (
                <div className="mt-2 space-y-2">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2"
                    >
                      <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                        {item.vehicle?.name || '—'}
                      </Text>
                      {item.link ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-0.5 inline-flex items-start gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline break-all"
                        >
                          {item.link}
                          <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 mt-0.5" />
                        </a>
                      ) : (
                        <Text size="sm" color="muted" className="mt-0.5">
                          —
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClippingDetalheModal
